import React, { useState, useEffect, useRef, useCallback } from "react";
import { LanguageProvider } from "./context/LanguageContext";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Skills from "./components/Skills";
import Experience from "./components/Experience";
import Projects from "./components/Projects";
import About from "./components/About";
import Footer from "./components/Footer";
import SplashScreen from "./components/SplashScreen";
import "./index.css";

function GlobalParticles() {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const mouse = useRef({ x: -1000, y: -1000 });
  const raf = useRef(null);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const count = Math.min(Math.floor((W * H) / 12000), 120);
    particles.current = Array.from({ length: count }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.8 + 0.6,
    }));
  }, []);

  useEffect(() => {
    init();
    let rt;
    const onResize = () => {
      clearTimeout(rt);
      rt = setTimeout(init, 250);
    };
    window.addEventListener("resize", onResize);

    const handleMouse = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
    };
    const handleLeave = () => {
      mouse.current = { x: -1000, y: -1000 };
    };
    window.addEventListener("mousemove", handleMouse);
    window.addEventListener("mouseleave", handleLeave);

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;
      ctx.clearRect(0, 0, W, H);

      const pts = particles.current;
      const linkDist = 120;

      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;

        const dx = p.x - mouse.current.x;
        const dy = p.y - mouse.current.y;
        const md = Math.sqrt(dx * dx + dy * dy);
        if (md < 100) {
          p.vx += (dx / md) * 0.15;
          p.vy += (dy / md) * 0.15;
        }
        const sp = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (sp > 1.2) {
          p.vx = (p.vx / sp) * 1.2;
          p.vy = (p.vy / sp) * 1.2;
        }
      });

      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < linkDist) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(222,184,135,${0.15 * (1 - d / linkDist)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      pts.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(222,184,135,0.6)";
        ctx.fill();
      });

      raf.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", handleMouse);
      window.removeEventListener("mouseleave", handleLeave);
    };
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
}

function quadBezierPoint(p0, p1, p2, t) {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
  };
}

function quadBezierLength(p0, p1, p2) {
  let len = 0;
  let prev = p0;
  const steps = 20;
  for (let i = 1; i <= steps; i++) {
    const pt = quadBezierPoint(p0, p1, p2, i / steps);
    const dx = pt.x - prev.x;
    const dy = pt.y - prev.y;
    len += Math.sqrt(dx * dx + dy * dy);
    prev = pt;
  }
  return len;
}

function buildSegments(sections, pageW, isMobile) {
  const segs = [];

  if (isMobile) {
    const x = 16;
    const firstTop = sections[0].offsetTop;
    const lastBot =
      sections[sections.length - 1].offsetTop +
      sections[sections.length - 1].offsetHeight;
    segs.push({
      type: "line",
      x1: x,
      y1: firstTop,
      x2: x,
      y2: lastBot,
      length: Math.abs(lastBot - firstTop),
    });
    return segs;
  }

  const margin = Math.max(28, Math.min(60, pageW * 0.035));
  const LX = margin;
  const RX = pageW - margin;
  const R = Math.max(30, Math.min(80, pageW * 0.05));
  let side = "left";
  let curY;

  sections.forEach((sec, i) => {
    const top = sec.offsetTop;
    const bot = top + sec.offsetHeight;
    const x = side === "left" ? LX : RX;

    if (i === 0) {
      curY = top;
      const segLen = Math.max(0, bot - R - top);
      segs.push({
        type: "line",
        x1: x,
        y1: top,
        x2: x,
        y2: bot - R,
        length: segLen,
      });
      curY = bot - R;
    } else {
      const prevX = side === "left" ? RX : LX;
      const bridgeY = top;
      const goingRight = x > prevX;

      const q1p0 = { x: prevX, y: curY };
      const q1p1 = { x: prevX, y: bridgeY };
      const q1p2 = { x: prevX + (goingRight ? R : -R), y: bridgeY };
      segs.push({
        type: "quad",
        p0: q1p0,
        p1: q1p1,
        p2: q1p2,
        length: quadBezierLength(q1p0, q1p1, q1p2),
      });

      const hx1 = prevX + (goingRight ? R : -R);
      const hx2 = x + (goingRight ? -R : R);
      segs.push({
        type: "line",
        x1: hx1,
        y1: bridgeY,
        x2: hx2,
        y2: bridgeY,
        length: Math.abs(hx2 - hx1),
      });

      const q2p0 = { x: hx2, y: bridgeY };
      const q2p1 = { x: x, y: bridgeY };
      const q2p2 = { x: x, y: bridgeY + R };
      segs.push({
        type: "quad",
        p0: q2p0,
        p1: q2p1,
        p2: q2p2,
        length: quadBezierLength(q2p0, q2p1, q2p2),
      });

      const vTop = bridgeY + R;
      const vBot = bot - R;
      segs.push({
        type: "line",
        x1: x,
        y1: vTop,
        x2: x,
        y2: vBot,
        length: Math.max(0, vBot - vTop),
      });

      curY = vBot;
    }
    side = side === "left" ? "right" : "left";
  });

  return segs;
}

function getPointOnSegments(segments, dist) {
  let remaining = dist;
  for (const seg of segments) {
    if (remaining <= seg.length) {
      const t = seg.length > 0 ? remaining / seg.length : 0;
      if (seg.type === "line") {
        return {
          x: seg.x1 + (seg.x2 - seg.x1) * t,
          y: seg.y1 + (seg.y2 - seg.y1) * t,
        };
      }
      return quadBezierPoint(seg.p0, seg.p1, seg.p2, t);
    }
    remaining -= seg.length;
  }
  const last = segments[segments.length - 1];
  if (!last) return { x: 0, y: 0 };
  if (last.type === "line") return { x: last.x2, y: last.y2 };
  return quadBezierPoint(last.p0, last.p1, last.p2, 1);
}

function segmentsToD(segments) {
  if (!segments.length) return "";
  let d = "";
  segments.forEach((seg, i) => {
    if (seg.type === "line") {
      if (i === 0) d += `M ${seg.x1} ${seg.y1} `;
      d += `L ${seg.x2} ${seg.y2} `;
    } else {
      if (i === 0) d += `M ${seg.p0.x} ${seg.p0.y} `;
      d += `Q ${seg.p1.x} ${seg.p1.y} ${seg.p2.x} ${seg.p2.y} `;
    }
  });
  return d.trim();
}

function ZigzagConnector() {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const pulseRef = useRef(null);
  const geoRef = useRef({ segments: [], totalLen: 0 });

  useEffect(() => {
    const build = () => {
      const wrap = wrapRef.current;
      const svg = svgRef.current;
      if (!wrap || !svg) return;

      const app = wrap.parentElement;
      if (!app) return;
      const sections = app.querySelectorAll("[data-section]");
      if (sections.length < 2) return;

      const pageW = app.scrollWidth;
      const pageH = app.scrollHeight;
      if (pageW === 0 || pageH === 0) return;

      const isMobile = pageW <= 600;

      svg.setAttribute("viewBox", `0 0 ${pageW} ${pageH}`);
      svg.style.width = pageW + "px";
      svg.style.height = pageH + "px";

      const segments = buildSegments(Array.from(sections), pageW, isMobile);
      const totalLen = segments.reduce((s, seg) => s + seg.length, 0);
      geoRef.current = { segments, totalLen };

      const d = segmentsToD(segments);

      // Set glow paths + main path
      const glowOuter = svg.querySelector("#zz-glow-outer");
      const glowInner = svg.querySelector("#zz-glow-inner");
      const mainPath = svg.querySelector("#zz-main");

      const sw = isMobile ? 1.5 : Math.max(1.5, Math.min(2, pageW * 0.0015));

      if (glowOuter) {
        glowOuter.setAttribute("d", d);
        glowOuter.setAttribute("stroke-width", sw * 5);
      }
      if (glowInner) {
        glowInner.setAttribute("d", d);
        glowInner.setAttribute("stroke-width", sw * 1);
      }
      if (mainPath) {
        mainPath.setAttribute("d", d);
        mainPath.setAttribute("stroke-width", sw);
      }

      // Dots at section junctions
      svg.querySelectorAll(".zz-dot").forEach((el) => el.remove());

      if (!isMobile) {
        let dotSide = "left";
        const dotMargin = Math.max(28, Math.min(60, pageW * 0.035));
        const dotLX = dotMargin;
        const dotRX = pageW - dotMargin;
        sections.forEach((sec) => {
          const top = sec.offsetTop;
          const bot = top + sec.offsetHeight;
          const x = dotSide === "left" ? dotLX : dotRX;
          [top, bot].forEach((cy) => {
            const c = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "circle",
            );
            c.setAttribute("cx", x);
            c.setAttribute("cy", cy);
            c.setAttribute("r", "4");
            c.setAttribute("fill", "var(--accent)");
            c.setAttribute("opacity", "0.6");
            c.setAttribute("class", "zz-dot");
            svg.appendChild(c);
          });
          dotSide = dotSide === "left" ? "right" : "left";
        });
      }
    };

    // Scroll-driven pulse — pure JS geometry, zero SVG API
    let scrollRaf;
    const updatePulse = () => {
      const { segments, totalLen } = geoRef.current;
      if (!segments.length || totalLen <= 0) return;

      const el = pulseRef.current;
      if (!el) return;

      const scrollTop = window.pageYOffset || window.scrollY || 0;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        docHeight > 0 ? Math.min(Math.max(scrollTop / docHeight, 0), 1) : 0;

      const pt = getPointOnSegments(segments, progress * totalLen);
      el.style.transform = `translate(${pt.x}px, ${pt.y}px) translate(-50%, -50%)`;
    };

    const onScroll = () => {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(updatePulse);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const t1 = setTimeout(() => {
      build();
      updatePulse();
    }, 400);
    const t2 = setTimeout(() => {
      build();
      updatePulse();
    }, 1500);

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        build();
        updatePulse();
      }, 250);
    };
    window.addEventListener("resize", onResize);

    const app = wrapRef.current?.parentElement;
    let resizeObs;
    let obsTimer;
    if (app && typeof ResizeObserver !== "undefined") {
      resizeObs = new ResizeObserver(() => {
        clearTimeout(obsTimer);
        obsTimer = setTimeout(() => {
          build();
          updatePulse();
        }, 350);
      });
      resizeObs.observe(app);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(scrollRaf);
      clearTimeout(resizeTimer);
      clearTimeout(obsTimer);
      clearTimeout(t1);
      clearTimeout(t2);
      if (resizeObs) resizeObs.disconnect();
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="zigzag-wrap"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
        overflow: "hidden",
        /* Fondu dégradé haut & bas */
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent 0%, black 4%, black 96%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, transparent 0%, black 4%, black 96%, transparent 100%)",
      }}
    >
      {/* Single SVG — NO filters, NO defs, NO blur */}
      <svg
        ref={svgRef}
        style={{ position: "absolute", top: 0, left: 0 }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Glow: outer soft layer */}
        <path
          id="zz-glow-outer"
          d=""
          fill="none"
          stroke="var(--accent)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.06"
        />
        {/* Glow: inner layer */}
        <path
          id="zz-glow-inner"
          d=""
          fill="none"
          stroke="var(--accent)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.12"
        />
        {/* Main visible line */}
        <path
          id="zz-main"
          d=""
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.35"
        />
      </svg>

      {/* Pulse: CSS div with box-shadow glow — works on all browsers */}
      <div
        ref={pulseRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: "var(--accent)",
          boxShadow: "0 0 8px 3px var(--accent), 0 0 20px 8px var(--accent)",
          opacity: 0.9,
          willChange: "transform",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("light-mode", !darkMode);
  }, [darkMode]);

  if (showSplash) return <SplashScreen />;

  return (
    <LanguageProvider>
      <div className={`app ${darkMode ? "" : "light-mode"}`}>
        <GlobalParticles />
        <ZigzagConnector />
        <Navbar darkMode={darkMode} setDarkMode={setDarkMode} />

        <div data-section="hero">
          <Hero />
        </div>
        <div data-section="skills">
          <Skills />
        </div>
        <div data-section="experience">
          <Experience />
        </div>
        <div data-section="projects">
          <Projects />
        </div>
        <div data-section="about">
          <About />
        </div>
        <Footer />
      </div>
    </LanguageProvider>
  );
}

export default App;
